/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import {FC, useState} from 'react'
import axios from 'axios'
import {useAuth} from '../../../../app/modules/auth'
import {API_URL, isOnline} from '../../../helpers'
import {
  businessOptionData,
  useBusinessSwitcherLabels,
} from '../../../../app/modules/apps/shared/business-switcher/businessSwitcherLabels'
import {
  BusinessOption,
  businessMatchesSearch,
} from '../../../../app/modules/apps/shared/business-switcher/BusinessOption'
import {readBusinessChangeError} from '../../../../app/modules/apps/shared/business-switcher/BusinessSelect'
import {AdminCompanySelector} from '../../../../app/modules/apps/shared/admin-company-selector/AdminCompanySelector'
import {confirmLeavingUnsavedWork} from '../../../../app/modules/apps/shared/business-switcher/unsavedWork'
import './SelectCompany.scss'
import {staffPicksBusiness} from '../../../../app/modules/auth/core/staffAccess'

/** A longer list gets a search box. */
const SEARCH_FROM = 5

/**
 * «کسب‌وکار» in the profile menu: a submenu that switches the selected business (hover on a desktop, a tap on a
 * phone). A user picks among their own businesses; staff granted «کسب‌وکارها» search every business, or go back
 * to all of them. The page reloads with the chosen business, as the other pickers do.
 */
const SelectCompany: FC = () => {
  const {currentUser, setCurrentUser, saveAuth} = useAuth()
  const isAdmin = staffPicksBusiness(currentUser)
  const businesses = currentUser?.cpl || []
  const shown = isOnline && !!currentUser && !currentUser.support && (isAdmin || businesses.length > 0)
  const labels = useBusinessSwitcherLabels(shown && !isAdmin ? businesses : undefined)
  const [search, setSearch] = useState('')
  const [changing, setChanging] = useState<number>()
  const [error, setError] = useState('')
  const selectedId = Number(currentUser?.cpi || 0)

  if (!shown) return null

  const setCompany = (companyId: number) => {
    if (changing !== undefined || companyId === selectedId) return
    if (!confirmLeavingUnsavedWork()) return
    setChanging(companyId)
    setError('')
    axios
      .post(`${API_URL}/auth/changeCompany`, {compnyId: companyId})
      .then((response) => {
        saveAuth(response.data)
        setCurrentUser(response.data)
        window.location.reload()
      })
      .catch((changeError) => {
        setChanging(undefined)
        setError(readBusinessChangeError(changeError))
      })
  }

  // The name and label on one line; the taxpayer type and the economic code under them. The search matches any.
  const optionOf = (company: (typeof businesses)[number]) => businessOptionData(company, labels[company.id])
  const currentName =
    labels[selectedId]?.name ||
    businesses.find((company) => company.id === selectedId)?.nm ||
    (isAdmin ? (selectedId > 0 ? `کسب‌وکار ${selectedId}` : 'همهٔ کسب‌وکارها') : 'انتخاب نشده')
  const term = search.trim()
  const visible = term ? businesses.filter((company) => businessMatchesSearch(optionOf(company), term)) : businesses

  return (
    <div
      className='menu-item px-5'
      data-kt-menu-trigger="{default: 'click', lg: 'hover'}"
      data-kt-menu-placement='left-start'
      data-kt-menu-flip='bottom'
    >
      <a href='#' className='menu-link px-5' onClick={(event) => event.preventDefault()} aria-haspopup='true'>
        <i className='las la-building fs-3 me-2' aria-hidden='true' />
        <span className='menu-title d-flex flex-column min-w-0'>
          <span>کسب‌وکار</span>
          <span className='scm__current' title={currentName}>
            {currentName}
          </span>
        </span>
        <span className='menu-arrow'></span>
      </a>

      <div className='menu-sub menu-sub-dropdown w-300px py-3 scm'>
        <div className='scm__head'>
          <i className='las la-exchange-alt' aria-hidden='true' />
          انتخاب کسب‌وکار
        </div>

        {isAdmin ? (
          <div className='px-4'>
            {/* Every business: the administrator's own search (all businesses when cleared). */}
            <AdminCompanySelector placeholder='جست‌وجوی نام، شناسه یا کد اقتصادی' onError={setError} />
            {selectedId > 0 && (
              <button type='button' className='btn btn-sm btn-light w-100 mt-2' onClick={() => setCompany(0)} disabled={changing !== undefined}>
                {changing === 0 ? <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' /> : <i className='las la-globe me-1' aria-hidden='true' />}
                همهٔ کسب‌وکارها
              </button>
            )}
          </div>
        ) : (
          <>
            {businesses.length >= SEARCH_FROM && (
              <div className='px-4 pb-2'>
                <input
                  className='form-control form-control-sm'
                  placeholder='نام، برچسب یا کد اقتصادی'
                  aria-label='جست‌وجوی کسب‌وکار'
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                />
              </div>
            )}
            <div className='scm__list' role='list'>
              {visible.map((company) => {
                const active = company.id === selectedId
                return (
                  <div className='menu-item px-3' key={company.id} role='listitem'>
                    <a
                      href='#'
                      className={clsx('menu-link px-4 scm__item', {active})}
                      aria-current={active ? 'true' : undefined}
                      onClick={(event) => {
                        event.preventDefault()
                        setCompany(company.id)
                      }}
                    >
                      <span className={clsx('scm__mark', {'is-active': active})} aria-hidden='true'>
                        {changing === company.id ? (
                          <span className='spinner-border spinner-border-sm' />
                        ) : (
                          <i className={clsx('las', active ? 'la-check' : 'la-building')} />
                        )}
                      </span>
                      <BusinessOption className='scm__text' business={optionOf(company)} />
                    </a>
                  </div>
                )
              })}
              {visible.length === 0 && <div className='scm__empty'>کسب‌وکاری با این نام یا کد پیدا نشد.</div>}
            </div>
          </>
        )}

        {error && (
          <div className='text-danger fs-8 px-4 pt-2' role='alert'>
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

export {SelectCompany}
